import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import packageJson from '../../../package.json';

/**
 * SearchSyntaxCode component
 * Renders search syntax with proper highlighting
 */
const SearchSyntaxCode = ({ children }) => {
  // Process children to extract operator and value
  // Format should be "operator:value" like "from:username"
  let operator = '';
  let value = '';
  
  if (typeof children === 'string') {
    const colonIndex = children.indexOf(':');
    if (colonIndex !== -1) {
      operator = children.substring(0, colonIndex + 1); // Include the colon
      value = children.substring(colonIndex + 1);
    } else {
      // No operator, treat as just value
      value = children;
    }
  }
  
  return (
    <code className="search-syntax">
      {operator && <span className="search-operator">{operator}</span>}
      <span className="search-value">{value}</span>
    </code>
  );
};

/**
 * SearchTips Component
 * Shows a collapsible panel with search tips and examples
 */
const SearchTips = () => {
  const [showSearchTips, setShowSearchTips] = useState(false);

  return (
    <div className="mt-2 text-center">
      <button
        onClick={() => setShowSearchTips(!showSearchTips)}
        className="text-sm text-blue-500 hover:underline flex items-center justify-center mx-auto"
      >
        <span>Search tips</span>
        <ChevronDown 
          className={`h-4 w-4 ml-1 transition-transform ${showSearchTips ? 'rotate-180' : ''}`}
        />
      </button>

      {showSearchTips && (
        <div className="mt-2 p-4 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 text-left text-sm animate-fadeIn">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="font-medium">Basic Search</div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Type any word to search content:<br />
                <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">javascript</code>
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                For exact phrases, use quotes:<br />
                <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">"machine learning"</code>
              </div>
              <div className="font-medium mt-3">User Filter <span className="text-xs font-normal text-blue-500">(with suggestions)</span></div>
              <div className="text-gray-600 dark:text-gray-300 mt-1">
                <code className="bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">from:username</code>
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Shows saves from specific users<br />
                Example: <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">from:johndoe</code>
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                For usernames with spaces:<br />
                <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">from:"John Doe"</code>
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Multiple users:<br />
                <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">from:user1 from:user2</code>
              </div>
            </div>

            <div>
              <div className="font-medium">Advanced Operators <span className="text-xs font-normal text-blue-500">(with suggestions)</span></div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                <span className="text-blue-500 dark:text-blue-400 font-medium">Note:</span> Parentheses are optional
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Filter by type:<br />
                <SearchSyntaxCode>is:reddit</SearchSyntaxCode>
                <span className="ml-1"><SearchSyntaxCode>is:like</SearchSyntaxCode></span>
                <span className="ml-1"><SearchSyntaxCode>is:bookmark</SearchSyntaxCode></span>
                <span className="ml-1"><SearchSyntaxCode>is:roam</SearchSyntaxCode></span>
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Filter by subreddit:<br />
                <SearchSyntaxCode>subreddit:askreddit</SearchSyntaxCode>
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Filter by namespace (Roam only):<br />
                <SearchSyntaxCode>namespace:book</SearchSyntaxCode>
                <span className="ml-1"><SearchSyntaxCode>namespace:movie</SearchSyntaxCode></span>
                <span className="ml-1"><SearchSyntaxCode>namespace:article</SearchSyntaxCode></span>
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Exclude words:<br />
                <SearchSyntaxCode>not:term</SearchSyntaxCode>
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Filter by skip category:<br />
                <SearchSyntaxCode>skip:manual</SearchSyntaxCode>
                <span className="ml-1"><SearchSyntaxCode>skip:low_quality</SearchSyntaxCode></span>
                <span className="ml-1"><SearchSyntaxCode>has:skip</SearchSyntaxCode></span>
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Exclude skipped tweets:<br />
                <SearchSyntaxCode>skip:none</SearchSyntaxCode>
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Roam notes support:<br />
                <span className="text-blue-500 dark:text-blue-400">[[Page references]]</span>, <span className="text-purple-500 dark:text-purple-400">#tags</span>, block embeds, and rich formatting
              </div>
            </div>
          </div>

          <div className="mt-3 text-xs text-gray-500 dark:text-gray-400 border-t border-gray-100 dark:border-gray-700 pt-2">
            <span className="block font-medium mb-1">Examples</span>
            <div className="mb-1">Basic search with multiple words: <SearchSyntaxCode>coffee books</SearchSyntaxCode></div>
            <div className="mb-1">User filter: <SearchSyntaxCode>from:simonw</SearchSyntaxCode></div>
            <div className="mb-1">Advanced search: <SearchSyntaxCode>python</SearchSyntaxCode> <SearchSyntaxCode>from:simonw</SearchSyntaxCode> <SearchSyntaxCode>is:like</SearchSyntaxCode></div>
            <div className="mb-1">Complex query: <SearchSyntaxCode>"machine learning"</SearchSyntaxCode> <SearchSyntaxCode>from:andrew</SearchSyntaxCode> <SearchSyntaxCode>is:bookmark</SearchSyntaxCode> <SearchSyntaxCode>not:course</SearchSyntaxCode></div>
            <div className="mb-1">Roam notes: <SearchSyntaxCode>is:roam</SearchSyntaxCode> <SearchSyntaxCode>philosophy</SearchSyntaxCode> - Search your Roam Research notes</div>
            <div className="mb-1">Roam namespaces: <SearchSyntaxCode>namespace:book</SearchSyntaxCode> - Find quotes from books only</div>
            <div className="mb-1">Type suggestions: Start typing <SearchSyntaxCode>is:</SearchSyntaxCode> to see content type options</div>
            <div className="mb-1">Subreddit suggestions: Start typing <SearchSyntaxCode>subreddit:</SearchSyntaxCode> to see options</div>
            <div className="mb-1">Namespace suggestions: Start typing <SearchSyntaxCode>namespace:</SearchSyntaxCode> to see available namespaces</div>
            <div className="mb-1">Skip category filter: <SearchSyntaxCode>skip:manual</SearchSyntaxCode> - Find manually skipped tweets</div>
            <div className="mb-1">Find all skipped: <SearchSyntaxCode>has:skip</SearchSyntaxCode> - Tweets with any skip category</div>
            <div className="mb-1">Exclude skipped: <SearchSyntaxCode>skip:none</SearchSyntaxCode> <SearchSyntaxCode>AI</SearchSyntaxCode> - Find non-skipped AI tweets</div>
            <div className="mb-1">Combined filters: <SearchSyntaxCode>from:elonmusk</SearchSyntaxCode> <SearchSyntaxCode>skip:low_quality</SearchSyntaxCode> - Low quality tweets from a specific user</div>
          </div>
          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 text-center">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
              Version {packageJson.version}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchTips;